/**
 * SDS advanced features — ported from Designer
 * Image quality check, grid guides, layer lock, opacity slider
 */

import { store } from '../core/store.js'
import { CONFIG } from '../core/config.js'
import { eventBus, EVENTS } from '../core/event-bus.js'
import { createLogger } from '../utils/logger.js'

const log = createLogger('image-processor')

// ========== Image quality check and auto-upscale ==========

async function autoUpscaleImage(imageUrl) {
  try {
    log.info('Auto-upscaling low quality image:', imageUrl)

    const response = await fetch(`${CONFIG.IMAGES_API}/upscale`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageUrl, scale: 2, format: 'webp' }),
    })

    if (!response.ok) throw new Error(`Upscale failed: ${response.status}`)

    const result = await response.json()

    if (result.success && result.upscaledUrl) {
      log.info('Image upscaled successfully')
      eventBus.emit(EVENTS.TOAST_SHOW, {
        message: 'Image quality improved! Changes will apply on next load.',
        type: 'success',
      })
      return result.upscaledUrl
    }
    throw new Error('Upscale result invalid')
  } catch (error) {
    log.error('Auto-upscale failed:', error)
    return null
  }
}

export function checkImageQuality() {
  const sdk = store.getState('design.sdkInstance')
  if (!sdk?.sarto) {
    console.warn('SDK not initialized')
    return
  }

  try {
    const activeLayer = sdk.sarto.getActiveLayer()
    if (!activeLayer) {
      eventBus.emit(EVENTS.TOAST_SHOW, { message: 'Please select an image layer first', type: 'warning' })
      return
    }

    if (activeLayer.type !== 'image') {
      eventBus.emit(EVENTS.TOAST_SHOW, { message: 'Please select an image layer', type: 'warning' })
      return
    }

    const imageUrl = activeLayer.src || activeLayer.url || activeLayer.imageUrl

    if (sdk.sarto.checkImageQuality) {
      sdk.sarto.checkImageQuality((result) => {
        if (result.status === 'success') {
          const quality = result.quality || 'unknown'
          const dpi = result.dpi || 'N/A'
          const message = `Image Quality: ${quality.toUpperCase()}\nDPI: ${dpi}`

          eventBus.emit(EVENTS.TOAST_SHOW, {
            message,
            type: quality === 'high' ? 'success' : 'warning',
          })

          if (quality !== 'high' && imageUrl) {
            autoUpscaleImage(imageUrl).catch(() => {})
          }
        } else {
          eventBus.emit(EVENTS.TOAST_SHOW, { message: 'Failed to check image quality', type: 'error' })
        }
      })
    } else {
      // Fallback: estimate from dimensions
      const width = activeLayer.width || 0
      const height = activeLayer.height || 0
      const area = width * height

      let quality = 'low'
      if (area > 1000000) quality = 'high'
      else if (area > 500000) quality = 'medium'

      const message = `Image Quality: ${quality.toUpperCase()}\nSize: ${width}x${height}px`
      eventBus.emit(EVENTS.TOAST_SHOW, {
        message,
        type: quality === 'high' ? 'success' : 'warning',
      })

      if (quality !== 'high' && imageUrl) {
        autoUpscaleImage(imageUrl).catch(() => {})
      }
    }
  } catch (error) {
    console.error('Failed to check image quality:', error)
    eventBus.emit(EVENTS.TOAST_SHOW, { message: 'Error checking image quality', type: 'error' })
  }
}

// ========== Grid guides ==========

let gridEnabled = false

export function toggleGrid() {
  const sdk = store.getState('design.sdkInstance')
  if (!sdk?.sarto) return

  try {
    gridEnabled = !gridEnabled

    if (sdk.sarto.toggleGrid) {
      sdk.sarto.toggleGrid(gridEnabled)
    } else if (sdk.sarto.showGrid && sdk.sarto.hideGrid) {
      gridEnabled ? sdk.sarto.showGrid() : sdk.sarto.hideGrid()
    }

    eventBus.emit(EVENTS.TOAST_SHOW, {
      message: gridEnabled ? 'Grid enabled' : 'Grid disabled',
      type: 'success',
    })
  } catch (error) {
    console.warn('Failed to toggle grid:', error)
  }
}

export function isGridEnabled() {
  return gridEnabled
}

// ========== Lock/Unlock ==========

export function lockLayer() {
  const sdk = store.getState('design.sdkInstance')
  if (!sdk?.sarto?.lockLayer) return
  try {
    sdk.sarto.lockLayer()
    eventBus.emit(EVENTS.TOAST_SHOW, { message: 'Layer locked', type: 'success' })
  } catch (error) {
    console.warn('Failed to lock layer:', error)
  }
}

export function unlockLayer() {
  const sdk = store.getState('design.sdkInstance')
  if (!sdk?.sarto?.unlockLayer) return
  try {
    sdk.sarto.unlockLayer()
    eventBus.emit(EVENTS.TOAST_SHOW, { message: 'Layer unlocked', type: 'success' })
  } catch (error) {
    console.warn('Failed to unlock layer:', error)
  }
}

// ========== Opacity slider ==========

export function showOpacitySlider() {
  const sdk = store.getState('design.sdkInstance')
  if (!sdk?.sarto) return

  try {
    const existing = document.getElementById('opacity-slider-container')
    if (existing) existing.remove()

    const sliderContainer = document.createElement('div')
    sliderContainer.id = 'opacity-slider-container'
    sliderContainer.className = 'opacity-slider-panel'

    sliderContainer.innerHTML = `
      <div style="display:flex;align-items:center;gap:12px;">
        <label class="opacity-slider-label">Opacity</label>
        <input type="range" id="opacity-slider" min="0" max="100" value="100" class="opacity-slider-input">
        <span id="opacity-value" class="opacity-slider-value">100%</span>
        <button id="opacity-close" class="opacity-slider-close">
          <i class="ph ph-x" style="font-size:16px;"></i>
        </button>
      </div>
    `

    document.body.appendChild(sliderContainer)

    const slider = document.getElementById('opacity-slider')
    const valueDisplay = document.getElementById('opacity-value')
    const closeBtn = document.getElementById('opacity-close')

    slider.addEventListener('input', (e) => {
      const value = e.target.value / 100
      valueDisplay.textContent = `${e.target.value}%`
      if (sdk.sarto.setOpacity) sdk.sarto.setOpacity(value)
    })

    closeBtn.addEventListener('click', () => sliderContainer.remove())
  } catch (error) {
    console.error('Failed to show opacity slider:', error)
  }
}

export default {
  checkImageQuality,
  toggleGrid,
  isGridEnabled,
  lockLayer,
  unlockLayer,
  showOpacitySlider,
}
